"use client";

import { createContext, useContext } from 'react';

type InventoryItem = Record<string, any>;

interface InventoryContextValue {
  inventory: InventoryItem[];
  getItemByBarcode: (barcode: string) => InventoryItem | undefined;
  getKitItems: (kitId: string) => InventoryItem[];
}

// Legacy V1/V2 inventory compatibility shim (2026-08-18). `items_v1_legacy` (and its
// entire CRUD surface — add/update/delete/retire/duplicate/kit-management, ~1000 lines
// of it) was removed here after confirming the table itself is fully dropped from the
// live DB and that every one of this context's write functions had zero remaining
// callers anywhere in the app (the V1/V2 edit UI that used to call them —
// EditItemModal.tsx, InventoryV2EditModal.tsx — was already deleted separately).
//
// This shim stays because 9 live 2.5-era files still destructure the 3 fields below —
// `inventory`, `getItemByBarcode`, `getKitItems` — as their fallback path for
// cross-referencing a barcode-keyed line against legacy stock before assuming it's an
// Inventory 2.5 item (e.g. PullSheetModal, CheckoutReceipt, staff/checkout,
// staff/requests). They already handle "not found" correctly (that's the normal case
// for every 2.5 item), so returning permanently-empty data here is the *correct*
// behavior now, not a degraded one — there is no legacy stock left to find. If none of
// those callers still need the fallback, this file can be deleted entirely along with
// its `InventoryProvider` mount in providers.tsx.
const InventoryContext = createContext<InventoryContextValue | null>(null);

export function InventoryProvider({ children }: { children: React.ReactNode }) {
  return (
    <InventoryContext.Provider
      value={{
        inventory: [],
        getItemByBarcode: () => undefined,
        getKitItems: () => [],
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
}
